import { NavLink } from "react-router";
import { cn } from "@/lib/cn";
import { useAppState } from "@/lib/queries";
import {
  visibleModules,
  type Audience,
  type UiAccess,
} from "@/lib/navigation";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  audience: Audience;
}

interface AppShape {
  uiAccess?: UiAccess;
}

/**
 * Sidebar is registry-driven — `lib/navigation.ts` declares the modules
 * available to each audience, and each module owns its own visibility
 * predicate against the `uiAccess` payload the server emits. Adding or
 * removing a module is a one-file change.
 */
export function Sidebar({ collapsed, onToggleCollapse, audience }: SidebarProps) {
  const { data: appRaw } = useAppState();
  const app = (appRaw ?? {}) as AppShape;
  const items = visibleModules(audience, app.uiAccess);

  return (
    <nav className="flex flex-col h-full bg-dw-bg border-r border-dw-border">
      {/* Collapse toggle */}
      <div className="flex items-center justify-end h-14 px-3 border-b border-dw-border">
        <button
          type="button"
          className="p-1.5 rounded-dw-sm text-dw-text-3 hover:text-dw-text hover:bg-dw-surface-h transition-colors"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            )}
          </svg>
        </button>
      </div>

      {/* Nav items */}
      <ul className="flex-1 py-2 space-y-0.5 px-2 overflow-y-auto">
        {items.map((item) => (
          <li key={item.key}>
            <NavLink
              to={item.path}
              aria-label={collapsed ? item.label : undefined}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-dw-sm px-3 py-2 text-sm font-body transition-colors",
                  isActive
                    ? "bg-dw-blue-soft text-dw-blue"
                    : "text-dw-text-3 hover:text-dw-text hover:bg-dw-surface-h",
                )
              }
            >
              <span className="text-base shrink-0 w-5 text-center" aria-hidden="true">
                {item.icon}
              </span>
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
